"use client";

import {NeoButton} from "@/components/forms";
import {NeoHeader, NeoLabel} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {CustomerType} from "@/libs/enums/live";
import {userService} from "@/service/users.service";
import {Spinner, Switch, Textarea} from "@nextui-org/react";
import React, {useCallback, useEffect, useState} from "react";
import toast from "react-hot-toast";

const MIN_COMMENT_LENGTH = 5;

type StatusHistoryEntry = {
  id: string | number;
  status: boolean | null;
  comments: string;
  dateTime: number | string | null;
  updatedBy?: string;
};

type ActiveInactiveUserProps = {
  selectedRow?: any;
  onClose?: () => void;
};

const toDate = (value: number | string | null): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(
    typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value: number | string | null) =>
  toDate(value)?.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }) ?? "-";

const formatTime = (value: number | string | null) =>
  toDate(value)?.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }) ?? "-";

export const ActiveInactiveUser: React.FC<ActiveInactiveUserProps> = ({
  selectedRow,
  onClose
}) => {
  const {filterContext} = useNeoContext();
  const userId = selectedRow?.userId ?? selectedRow?.id;
  const isSchool = filterContext?.client?.customerType === CustomerType.SCHOOL;

  const [isActive, setIsActive] = useState<boolean>(
    Number(selectedRow?.status ?? selectedRow?.isActive ?? 1) === 1
  );
  const [comments, setComments] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const trimmed = comments.trim();
  const commentError =
    trimmed.length < MIN_COMMENT_LENGTH
      ? `Comment must be at least ${MIN_COMMENT_LENGTH} characters`
      : null;

  const loadHistory = useCallback(async () => {
    if (!userId) return;
    setLoadingHistory(true);
    try {
      const rows = await userService.onGetUserStatusHistory(userId);
      setHistory(rows);
    } finally {
      setLoadingHistory(false);
    }
  }, [userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onSubmit = async () => {
    setTouched(true);
    if (commentError || !userId) return;
    setSubmitting(true);
    try {
      const response = await userService.onUpdateUserStatus(
        userId,
        isActive ? 1 : 0,
        trimmed
      );
      if (response?.status === "success") {
        toast.success(response?.message || "User status updated successfully");
        setComments("");
        setTouched(false);
        await loadHistory();
      } else {
        toast.error(response?.message || "Failed to update user status");
      }
    } catch {
      toast.error("Failed to update user status");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-3 pb-3">
      <div className="my-5 px-1">
        <NeoHeader message="Active / Inactive User" />
      </div>

      <div className="grid grid-cols-3 gap-4 px-1">
        <div>
          <NeoLabel label="Name" value={selectedRow?.name} />
        </div>
        <div>
          {isSchool ? (
            <NeoLabel label="Admission No" value={selectedRow?.admissionNo} />
          ) : (
            <NeoLabel
              label="Employee ID"
              value={selectedRow?.employeeId ?? selectedRow?.empId}
            />
          )}
        </div>
        <div>
          <NeoLabel label="User Type" value={selectedRow?.userType} />
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 items-center gap-4 px-1">
        <div>
          <NeoLabel label="Phone" value={selectedRow?.phone} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-2">
          <span
            className={`font-medium ${isActive ? "text-green-600" : "text-red-600"}`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
          <Switch
            size="sm"
            isSelected={isActive}
            onValueChange={setIsActive}
            aria-label="Toggle user active status"
          />
        </div>
      </div>

      <div className="mt-4">
        <Textarea
          variant="bordered"
          label="Comments"
          placeholder={`Enter comments (minimum ${MIN_COMMENT_LENGTH} characters)`}
          value={comments}
          onValueChange={setComments}
          onBlur={() => setTouched(true)}
          isInvalid={touched && !!commentError}
          errorMessage={touched ? commentError : undefined}
        />
      </div>

      <div className="mt-4 flex items-center justify-end gap-3">
        <NeoButton
          color="yellow"
          size="xl"
          onPress={onSubmit}
          isLoading={submitting}
        >
          Submit
        </NeoButton>
      </div>

      <div className="mt-6 border-t border-gray-200 pt-4">
        <h3 className="mb-2 text-base font-semibold text-gray-800">History</h3>
        {loadingHistory ? (
          <div className="flex justify-center py-6">
            <Spinner size="sm" />
          </div>
        ) : history.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            No history available
          </p>
        ) : (
          <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
            {history.map(entry => (
              <li
                key={entry.id}
                className="flex items-start justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-xs text-gray-500">
                    {formatDate(entry.dateTime)} · {formatTime(entry.dateTime)}
                  </div>
                  <p className="mt-1 text-sm text-gray-800">
                    {entry.comments || "-"}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right text-xs">
                  {entry.status !== null && (
                    <p
                      className={
                        entry.status ? "text-green-600" : "text-red-600"
                      }
                    >
                      {entry.status ? "Active" : "Inactive"}
                    </p>
                  )}
                  {entry.updatedBy && (
                    <p className="mt-1 text-gray-500">By {entry.updatedBy}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
